import { Sync } from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { Log, PMButton } from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";

import { formatDateTime } from "../../utils/format-date";

type LogsPageProps = {
  logs: Log[];
  isLoading: boolean;
  language?: string;
};

type LogsPageEventNames = "overlay:load-more-logs";

export const LogsPage: PimoReactComponent<
  LogsPageProps,
  LogsPageEventNames
> = ({ logs, isLoading, language, fireEvent }) => {
  /**
   * `updatedFieldName` on a log entry is often a raw entity field key (e.g.
   * `degreeOfCompletion`, `dcRelevance`) rather than the human label the rest
   * of the app shows for it. `getLocalizedMessage` returns the key unchanged
   * when it finds no translation, so entries that are already human-readable
   * (`formData:...`, a `field.label`, ...) pass through untouched.
   */
  const translateFieldName = (fieldName: string) =>
    getLocalizedMessage(language || "en", fieldName);

  const handleLoadMore = () => {
    if (!isLoading) {
      fireEvent?.("overlay:load-more-logs");
    }
  };

  return (
    <Box sx={{ padding: "16px 16px 0 16px" }}>
      <List sx={{ padding: 0 }}>
        {logs?.map((entry, index) => (
          <ListItem
            key={`${entry.createdAt}-${index}`}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignContent: "center",
              gap: 2,
              width: "100%",
              padding: "8px 0",
            }}
          >
            <Accordion
              sx={{
                boxShadow: "none",
                width: "100%",
                border: `1px solid ${grey[200]}`,
                borderRadius: "8px",
                "&:before": {
                  display: "none",
                },
                "&.Mui-expanded": {
                  margin: "8px 0",
                },
              }}
            >
              <AccordionSummary
                sx={{
                  padding: "12px 16px",
                  minHeight: "auto",
                  "&.Mui-expanded": {
                    minHeight: "auto",
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "0",
                    "&.Mui-expanded": {
                      margin: "0",
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <Sync sx={{ color: "#2196F3", fontSize: "20px" }} />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                    }}
                  >
                    {(!entry.action || entry.action === "edit") && (
                      <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                        <Typography component="span" sx={{ fontWeight: 600 }}>
                          {entry.initiator}
                        </Typography>{" "}
                        updated {entry.objectName ? `${entry.objectName}` : ""}
                        <Typography
                          component="span"
                          sx={{ color: "#2196F3", fontWeight: 500 }}
                        >
                          {" "}
                          - {translateFieldName(entry.updatedFieldName)}
                        </Typography>
                      </Typography>
                    )}
                    {entry.action === "create" && (
                      <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                        <Typography component="span" sx={{ fontWeight: 600 }}>
                          {entry.initiator}
                        </Typography>{" "}
                        created in{" "}
                        {entry.objectName ? `${entry.objectName}` : ""}
                        <Typography
                          component="span"
                          sx={{ color: "#2196F3", fontWeight: 500 }}
                        >
                          {" "}
                          - {translateFieldName(entry.updatedFieldName)}
                        </Typography>
                      </Typography>
                    )}
                    {entry.action === "delete" && (
                      <Typography sx={{ fontWeight: 500, fontSize: "14px" }}>
                        <Typography component="span" sx={{ fontWeight: 600 }}>
                          {entry.initiator}
                        </Typography>{" "}
                        deleted from{" "}
                        {entry.objectName ? `${entry.objectName}` : ""}
                        <Typography
                          component="span"
                          sx={{ color: "#2196F3", fontWeight: 500 }}
                        >
                          {" "}
                          - {translateFieldName(entry.updatedFieldName)}
                        </Typography>
                      </Typography>
                    )}
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {formatDateTime(entry.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              </AccordionSummary>
              {(entry.previousValue || entry.newValue) && (
                <AccordionDetails sx={{ padding: "0 16px 16px 16px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      width: "100%",
                      justifyContent: "space-between",
                      gap: 2,
                      marginLeft: "36px",
                    }}
                  >
                    {[entry.previousValue, entry.newValue].map(
                      (value, index) => (
                        <Box
                          key={`${value}-${index}`}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: "48%",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "12px",
                              marginBottom: "8px",
                              color: grey[700],
                            }}
                          >
                            {index === 0 ? "Previous" : "New"} Value
                          </Typography>
                          <Typography
                            sx={{
                              border: "1px solid",
                              borderColor: grey[200],
                              borderRadius: "6px",
                              padding: "12px",
                              overflow: "hidden",
                              wordBreak: "break-word",
                              backgroundColor: grey[50],
                              fontSize: "13px",
                              lineHeight: 1.4,
                            }}
                          >
                            {value}
                          </Typography>
                        </Box>
                      )
                    )}
                  </Box>
                </AccordionDetails>
              )}
            </Accordion>
          </ListItem>
        ))}
      </List>
      <Box display="flex" flexDirection="column" alignItems="center" p={2}>
        {!isLoading && logs.length > 0 && (
          <PMButton
            variant="outlined"
            size="medium"
            onClick={handleLoadMore}
            text="Load More Logs"
          />
        )}
        {isLoading && <CircularProgress sx={{ color: "#2196F3" }} />}
      </Box>
    </Box>
  );
};
